<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { computedAsync } from '@vueuse/core';

import {
  useStatsigEventsStore,
  useBrowserStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { TransportType } from '@libs/trip-planner-interfaces';
import { isExperimentAsync } from '@libs/feature-toggle';

import type { AlternativeItineraryFlightSegment } from '../../../models';
import { useTripOverviewPageStore } from '../../../stores';
import ChangeProductLink from '../ChangeProductLink.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';

import AlternativeFlightProductCard from './AlternativeFlightProductCard.vue';
import FlightSegmentHeader from './FlightSegmentHeader.vue';
import ExperimentalFlightSegmentHeader from './ExperimentalFlightSegmentHeader.vue';

interface AlternativeFlightSegmentProps {
  segment: AlternativeItineraryFlightSegment;
}

const props = defineProps<AlternativeFlightSegmentProps>();

interface AlternativeFlightSegmentEmits {
  (event: 'show-details'): void;
  (event: 'confirm-alternative'): void;
  (eventName: 'remove-filter', id: string): void;
  (eventName: 'show-more-filters'): void;
}

const emit = defineEmits<AlternativeFlightSegmentEmits>();

const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const recipeStore = useRecipeStore();
const browserStore = useBrowserStore();

const isFirstTransportSegment = computed<boolean>(
  () =>
    recipeStore.flightOnlyRecipe[0]?.ingredientId === props.segment.ingredientId
);

const isMobileHeaderExperiment = computedAsync<boolean>(
  () => isExperimentAsync('mobile_flight_header'),
  false
);

const shouldShowExperiment = computed(
  () => isMobileHeaderExperiment.value && browserStore.isSmallerThan600px
);

function removeFlight(): void {
  tripOverviewPageStore.selectTransport(props.segment, TransportType.None);
}

onMounted(() => {
  statsigEventsStore.logTripOverviewAlternativeFlightDisplayed();
});
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    :is-experimental-header-enabled="shouldShowExperiment"
    :class="{
      'has-experimental-header': shouldShowExperiment,
      'hide-timeline':
        props.segment.suggestionType === undefined &&
        isFirstTransportSegment &&
        shouldShowExperiment,
    }"
  >
    <template v-if="shouldShowExperiment" #experimental-header>
      <ExperimentalFlightSegmentHeader
        :segment="props.segment"
        @show-more-filters="emit('show-more-filters')"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('show-more-filters');
        "
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>
    <template v-if="!shouldShowExperiment" #dropdown>
      <ItineraryTransportSelector
        :transport-type="TransportType.Flight"
        :disabled="false"
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />
    </template>

    <template v-if="!shouldShowExperiment" #header>
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="alternative-flight-segment-content-wrapper">
        <AlternativeFlightProductCard
          :product-card-data="props.segment.productCardData"
          @show-details="emit('show-details')"
          @confirm-alternative="emit('confirm-alternative')"
          @remove-flight="removeFlight()"
        />

        <ChangeProductLink
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.alternative-flight-segment-content-wrapper {
  display: flex;
  flex-flow: column;
  gap: var(--spacing-12);

  .change-product-link {
    align-self: flex-end;
  }
}

.alternative-flight-segment-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-12);

  .separator {
    border-right: 1px solid var(--color-neutral-30);
    height: 100%;
  }

  .suggestion-type {
    color: var(--color-neutral-20);
    border: none;
    @include font-label-s-light;
  }

  @include for-less-or-equal-than-599-px {
    width: 100%;
    .separator {
      display: none;
    }
  }
}
</style>
